import { z } from 'zod';

import { AccountIdSchema } from './account';
import { InvestmentAllocationResponseSchema } from './investmentAllocation';
import {
  InvestmentAccountReturnsSchema,
  InvestmentCurrencyReturnsSchema,
} from './investmentReturns';

const ReturnMetricsSchema = z.object({
  invested: z.string().nullable(),
  income: z.string(),
  realisedPnl: z.string().nullable(),
  totalReturn: z.string().nullable(),
  totalReturnPct: z.string().nullable(),
  returnHoldingCount: z.number().int(),
  returnReconciledCount: z.number().int(),
});

export const UpdateInvestmentThemeSchema = z.object({
  accountId: AccountIdSchema,
  holdingId: z.string().cuid(),
  theme: z.string().trim().min(1).max(80).nullable(),
});

const MetricsSchema = z.object({
  value: z.string(),
  cost: z.string().nullable(),
  unrealizedPnl: z.string().nullable(),
  unrealizedPnlPct: z.string().nullable(),
  weightPct: z.string().nullable(),
});

export const InvestmentPortfolioHoldingSchema = MetricsSchema.merge(ReturnMetricsSchema).extend({
  identity: z.string(),
  symbol: z.string(),
  stock: z.string(),
  theme: z.string().nullable(),
  suggestedTheme: z.string().nullable(),
  suggestionReason: z.string().nullable(),
  accounts: z.array(
    z.object({
      accountId: z.string(),
      accountName: z.string(),
      holdingId: z.string(),
      value: z.string(),
      units: z.string().nullable(),
      unexplainedUnits: z.string().nullable(),
    }),
  ),
  missingThesisCount: z.number().int(),
  reviewDueCount: z.number().int(),
  brokenThesisCount: z.number().int(),
  aboveMaximumCount: z.number().int(),
});

export const InvestmentPortfolioResponseSchema = z.object({
  accounts: z.array(InvestmentAllocationResponseSchema),
  currencies: z.array(
    z.object({
      currency: z.string(),
      accountCount: z.number().int(),
      accountValue: z.string(),
      cash: z.string(),
      cashWeightPct: z.string().nullable(),
      holdingsValue: z.string(),
      cost: z.string().nullable(),
      unrealizedPnl: z.string().nullable(),
      unrealizedPnlPct: z.string().nullable(),
      topFiveWeightPct: z.string().nullable(),
      holdings: z.array(InvestmentPortfolioHoldingSchema),
      themes: z.array(
        MetricsSchema.merge(ReturnMetricsSchema).extend({
          theme: z.string().nullable(),
          holdingCount: z.number().int(),
        }),
      ),
      returns: InvestmentCurrencyReturnsSchema,
    }),
  ),
  accountReturns: z.array(InvestmentAccountReturnsSchema),
});

export type UpdateInvestmentThemeInput = z.infer<typeof UpdateInvestmentThemeSchema>;
export type InvestmentPortfolioHolding = z.infer<typeof InvestmentPortfolioHoldingSchema>;
export type InvestmentPortfolioResponse = z.infer<typeof InvestmentPortfolioResponseSchema>;
